import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Scale, ShieldCheck, FileLock2, AlertCircle } from 'lucide-react';
import { legalAPI } from '@/services/api';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const ICONS = {
	terms: Scale,
	privacy: ShieldCheck,
	gdpr: FileLock2,
};

const I18N_KEYS = {
	terms: 'legal.terms_title',
	privacy: 'legal.privacy_title',
	gdpr: 'legal.gdpr_title',
};

const SIBLINGS = ['terms', 'privacy', 'gdpr'];

export default function LegalPage({ slug }) {
	const { t, i18n } = useTranslation();
	const [doc, setDoc] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	useDocumentMeta({
		title: `${t(I18N_KEYS[slug] || 'legal.terms_title')} | Equi-Market`,
		description: t('legal.subtitle'),
	});

	useEffect(() => {
		setLoading(true);
		setError(false);
		const lang = (i18n.language || 'en').toLowerCase().split(/[-_@]/)[0];
		legalAPI
			.get(slug, lang)
			.then((r) => setDoc(r.data))
			.catch(() => setError(true))
			.finally(() => setLoading(false));
	}, [slug, i18n.language]);

	const Icon = ICONS[slug] || Scale;
	const fallbackUsed = doc && doc.lang !== (i18n.language || 'en').toLowerCase().split(/[-_@]/)[0];

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid={`legal-page-${slug}`}>
			{/* Header */}
			<div className="bg-white border-b border-[#E6E2D6]">
				<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
					<div className="flex items-center gap-3 mb-2">
						<Icon className="w-6 h-6 text-[#8B4513]" />
						<h1 className="text-3xl md:text-4xl font-medium text-[#1A3C34] font-['Playfair_Display']">
							{doc?.title || t(I18N_KEYS[slug] || 'legal.terms_title')}
						</h1>
					</div>
					{doc?.updated_at && (
						<p className="text-xs text-[#666055]">
							{t('legal.last_updated', { date: doc.updated_at.slice(0, 10) })}
						</p>
					)}

					{/* sibling links */}
					<nav className="mt-6 flex flex-wrap items-center gap-2 text-xs">
						{SIBLINGS.map((s) => (
							<Link
								key={s}
								to={`/${s}`}
								data-testid={`legal-nav-${s}`}
								className={`px-3 py-1.5 rounded-sm border transition-colors ${
									s === slug
										? 'bg-[#1A3C34] text-white border-[#1A3C34]'
										: 'bg-white text-[#1A3C34] border-[#E6E2D6] hover:bg-[#F2F0E9]'
								}`}
							>
								{t(I18N_KEYS[s])}
							</Link>
						))}
					</nav>
				</div>
			</div>

			{/* Body */}
			<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
				{loading ? (
					<div className="space-y-3">
						{[...Array(6)].map((_, i) => (
							<div key={i} className="h-6 bg-white border border-[#E6E2D6] rounded-sm img-skeleton" />
						))}
					</div>
				) : error ? (
					<div
						className="bg-white border border-[#E6E2D6] rounded-sm p-12 text-center text-sm text-[#666055]
						flex flex-col items-center gap-3"
						data-testid="legal-error"
					>
						<AlertCircle className="w-6 h-6 text-[#9B2C2C]" />
						{t('common.error')}
					</div>
				) : (
					<>
						{fallbackUsed && (
							<div
								className="mb-5 bg-[#FFF8E1] border border-[#E6D9A6] rounded-sm px-4 py-3 text-xs text-[#5C4A0A]"
								data-testid="legal-fallback-notice"
							>
								{t('legal.fallback_to_en')}
							</div>
						)}
						<article
							className="bg-white border border-[#E6E2D6] rounded-sm p-6 sm:p-8 legal-prose"
							data-testid="legal-body"
						>
							<ReactMarkdown
								components={ {
									a: ({ href, children }) => {
										const isExternal = href?.startsWith('http');
										return isExternal ? (
											<a href={ href } target="_blank" rel="noopener noreferrer">{ children }</a>
										) : (
											<a href={ href }>{ children }</a>
										);
									}
								} }>
								{ doc?.body || '' }
							</ReactMarkdown>
						</article>
					</>
				)}
			</div>
		</div>
	);
}